import { SEO } from '../components/SEO';

export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Learn how ByteJourney collects, uses, and protects your personal information. Read our comprehensive privacy policy."
        canonical="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: December 2025</p>

      <div className="prose prose-gray max-w-none space-y-6">
        <section>
          <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            Welcome to ByteJourney. We respect your privacy and are committed to protecting your personal data.
            This privacy policy explains how we collect, use, and safeguard your information when you use our platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you sign in with Google, we collect the following information:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Email address</strong> - Used to identify your account and for communication purposes</li>
            <li><strong>Name</strong> - Displayed as your author name on articles you publish</li>
            <li><strong>Profile picture</strong> - Displayed alongside your content and comments</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            We also collect content you create on our platform, including:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Articles you write and publish</li>
            <li>Comments you post</li>
            <li>Images you upload</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">We use your information to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Create and manage your account</li>
            <li>Display your name and profile picture on content you create</li>
            <li>Enable you to write, edit, and publish articles</li>
            <li>Allow you to comment on articles</li>
            <li>Improve our services and user experience</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">4. Data Storage and Security</h2>
          <p className="text-gray-700 leading-relaxed">
            Your data is stored securely using Cloudflare's infrastructure, including:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
            <li><strong>Cloudflare D1</strong> - For storing account information and articles</li>
            <li><strong>Cloudflare R2</strong> - For storing uploaded images</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            We implement industry-standard security measures to protect your data, including HTTPS encryption
            for all data transmission and secure authentication via Google OAuth 2.0.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">5. Data Sharing</h2>
          <p className="text-gray-700 leading-relaxed">
            We do not sell, trade, or rent your personal information to third parties. Your data may be shared only in the following circumstances:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
            <li><strong>Public content</strong> - Articles you publish as "public" are visible to all visitors</li>
            <li><strong>Service providers</strong> - We use Cloudflare for hosting and Google for authentication</li>
            <li><strong>Legal requirements</strong> - If required by law or to protect our rights</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">6. Your Rights</h2>
          <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Access your personal data</li>
            <li>Edit or delete your articles and comments</li>
            <li>Request deletion of your account and associated data</li>
            <li>Withdraw consent for data processing</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">7. Cookies</h2>
          <p className="text-gray-700 leading-relaxed">
            We use essential cookies to maintain your login session. We do not use tracking cookies or
            third-party analytics cookies. Your authentication token is stored in your browser's local storage.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">8. Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this privacy policy from time to time. We will notify you of any significant changes
            by posting the new policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">9. Contact Us</h2>
          <p className="text-gray-700 leading-relaxed">
            If you have any questions about this privacy policy or our data practices, please contact us
            through our platform.
          </p>
        </section>
      </div>
      </div>
    </>
  );
}
